import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  InfoSection,
  PropertySectionHeader as SectionHeader,
  PropertySectionTitle as SectionTitle,
  SectionIcon,
  SectionContent,
  PropertyDataGrid as DataGrid,
  PropertyDataRow as DataRow,
  DataValue,
} from '../../../molecules/PropertyPanelStyles'
import { DataLabelWithTooltip } from '../../../molecules/DataLabelWithTooltip'
import { Badge } from '../../../atoms/Badge'
import type { Land } from '@popety-io/types'
import { formatNumberSwiss } from '../../../../utils/number/formatUtils'

interface LandInformationProps {
  land: Land
}

export const LandInformation: React.FC<LandInformationProps> = ({ land }) => {
  const { t } = useTranslation('explore')
  const { t: tCommon } = useTranslation('common')
  // Groups already sum each member's official-area-first surface in the API.
  const surfaceArea =
    'total_area' in land
      ? (land.display_area ?? land.area)
      : (land.official_area ?? land.area)

  // Plot number, prefixed with the locality to mirror the panel header
  // ("Paudex 58"). Joins every parcel number for multi-parcel groupings.
  const plotNumbers = (land as any).all_code_numbers?.length
    ? (land as any).all_code_numbers.join(', ')
    : land.code_number
  const plotLocality = land.municipality_name || ''
  const plotNumberValue = plotLocality
    ? `${plotLocality} ${plotNumbers}`
    : plotNumbers

  return (
    <InfoSection>
      <SectionHeader>
        <SectionTitle>
          <SectionIcon>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </SectionIcon>
          {t('propertyTooltip.landInformation.title')}
        </SectionTitle>
      </SectionHeader>
      <SectionContent>
        <DataGrid>
          {(land.egrid_nb || ((land as any).all_egrids?.length ?? 0) > 0) && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('propertyTooltip.landInformation.egrid')}
                explanation={tCommon(
                  'propertyExplanations.landInformation.egrid'
                )}
              >
                {t('propertyTooltip.landInformation.egrid')}
              </DataLabelWithTooltip>
              <DataValue>
                {(land as any).all_egrids?.length
                  ? (land as any).all_egrids.join(', ')
                  : land.egrid_nb}
              </DataValue>
            </DataRow>
          )}
          {(land.code_number ||
            ((land as any).all_code_numbers?.length ?? 0) > 0) && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('propertyTooltip.landInformation.plotNumber')}
                explanation={tCommon(
                  'propertyExplanations.landInformation.plotNumber'
                )}
              >
                {t('propertyTooltip.landInformation.plotNumber')}
              </DataLabelWithTooltip>
              <DataValue>{plotNumberValue}</DataValue>
            </DataRow>
          )}
          {surfaceArea != null && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('propertyTooltip.landInformation.area')}
                explanation={tCommon(
                  'propertyExplanations.landInformation.area'
                )}
              >
                {t('propertyTooltip.landInformation.area')}
              </DataLabelWithTooltip>
              <DataValue $large>{formatNumberSwiss(surfaceArea)} m²</DataValue>
            </DataRow>
          )}
          <DataRow>
            <DataLabelWithTooltip
              label={t('propertyTooltip.landInformation.buildingArea')}
              explanation={tCommon(
                'propertyExplanations.landInformation.buildingArea'
              )}
            >
              {t('propertyTooltip.landInformation.buildingArea')}
            </DataLabelWithTooltip>
            <DataValue>
              {land.building_area === true ? (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="var(--success)"
                >
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                </svg>
              ) : (
                t('propertyTooltip.landInformation.notApplicable', 'N/A')
              )}
            </DataValue>
          </DataRow>
          {land.building_nb !== undefined && (
            <DataRow>
              <DataLabelWithTooltip
                label={t('propertyTooltip.landInformation.numberOfBuildings')}
                explanation={tCommon(
                  'propertyExplanations.landInformation.numberOfBuildings'
                )}
              >
                {t('propertyTooltip.landInformation.numberOfBuildings')}
              </DataLabelWithTooltip>
              <DataValue>{land.building_nb}</DataValue>
            </DataRow>
          )}
          {land.buildings && land.buildings.length > 0 && (
            <>
              <DataRow>
                <DataLabelWithTooltip
                  label={t('propertyTooltip.landInformation.buildingPeriod')}
                  explanation={tCommon(
                    'propertyExplanations.landInformation.buildingPeriod'
                  )}
                >
                  {t('propertyTooltip.landInformation.buildingPeriod')}
                </DataLabelWithTooltip>
                <DataValue>
                  {getBuildingPeriod(land.buildings) ||
                    t('propertyTooltip.landInformation.notApplicable', 'N/A')}
                </DataValue>
              </DataRow>
              <DataRow>
                <DataLabelWithTooltip
                  label={t('propertyTooltip.landInformation.lastRenovation')}
                  explanation={tCommon(
                    'propertyExplanations.landInformation.lastRenovation'
                  )}
                >
                  {t('propertyTooltip.landInformation.lastRenovation')}
                </DataLabelWithTooltip>
                <DataValue>
                  {getLastRenovationYear(land.buildings) ||
                    t('propertyTooltip.landInformation.notApplicable', 'N/A')}
                </DataValue>
              </DataRow>
              <DataRow>
                <DataLabelWithTooltip
                  label={t('propertyTooltip.landInformation.numberOfDwellings')}
                  explanation={tCommon(
                    'propertyExplanations.landInformation.numberOfDwellings'
                  )}
                >
                  {t('propertyTooltip.landInformation.numberOfDwellings')}
                </DataLabelWithTooltip>
                <DataValue>{calculateTotalUnits(land.buildings)}</DataValue>
              </DataRow>
              <DataRow>
                <DataLabelWithTooltip
                  label={t(
                    'propertyTooltip.landInformation.totalResidentialArea'
                  )}
                  explanation={tCommon(
                    'propertyExplanations.landInformation.totalResidentialArea'
                  )}
                >
                  {t('propertyTooltip.landInformation.totalResidentialArea')}
                </DataLabelWithTooltip>
                <DataValue>
                  {formatNumberSwiss(land.unit_total_area)} m²
                </DataValue>
              </DataRow>
            </>
          )}
          <DataRow>
            <DataLabelWithTooltip
              label={t('propertyTooltip.landInformation.propertyNature')}
              explanation={tCommon(
                'propertyExplanations.landInformation.propertyNature'
              )}
            >
              {t('propertyTooltip.landInformation.propertyNature')}
            </DataLabelWithTooltip>
            <DataValue>
              <Badge
                variant={
                  land.is_distinct_permanent_right ? 'warning' : 'default'
                }
                size="small"
              >
                {land.is_distinct_permanent_right
                  ? t('propertyTooltip.landInformation.propertyNatureDDP')
                  : t('propertyTooltip.landInformation.propertyNatureBF')}
              </Badge>
            </DataValue>
          </DataRow>
          <DataRow>
            <DataLabelWithTooltip
              label={t('propertyTooltip.landInformation.swimmingPool')}
              explanation={tCommon(
                'propertyExplanations.landInformation.swimmingPool'
              )}
            >
              {t('propertyTooltip.landInformation.swimmingPool')}
            </DataLabelWithTooltip>
            <DataValue>
              {land.has_swimming_pool == null ? (
                t('propertyTooltip.landInformation.notApplicable', 'N/A')
              ) : land.has_swimming_pool ? (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="var(--success)"
                >
                  <path
                    d="M20 6L9 17l-5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                </svg>
              ) : (
                tCommon('no', 'No')
              )}
            </DataValue>
          </DataRow>
        </DataGrid>
      </SectionContent>
    </InfoSection>
  )
}

// Helper functions
function getBuildingPeriod(buildings: any[]): string | null {
  const admins = buildings.flatMap((b) => b.buildings_administrative || [])
  const mins = admins
    .map((ba) => ba.building_year_min || ba.building_year)
    .filter((y) => y && y > 0)
  const maxs = admins
    .map((ba) => ba.building_year_max || ba.building_year)
    .filter((y) => y && y > 0)

  if (mins.length > 0) {
    const min = Math.min(...mins)
    const max = Math.max(...maxs)
    if (min === max) return String(min)
    return `${min} – ${max}`
  }

  const periods = admins.map((ba) => ba.building_period).filter(Boolean)
  if (periods.length > 0) {
    const allYears = periods.flatMap((p: string) =>
      p
        .split('-')
        .map(Number)
        .filter((y: number) => y > 0)
    )
    if (allYears.length > 0) {
      const min = Math.min(...allYears)
      const max = Math.max(...allYears)
      if (min === max) return `≤ ${min}`
      return `${min} – ${max}`
    }
    return periods[0]
  }

  return null
}

function getLastRenovationYear(buildings: any[]): number | null {
  const years = buildings
    .flatMap((b) => b.buildings_administrative || [])
    .map((ba) => ba.renovation_year_max || ba.renovation_year)
    .filter((y) => y && y > 0)

  if (years.length === 0) return null
  return Math.max(...years)
}

function calculateTotalUnits(buildings: any[]): number {
  return buildings
    .flatMap((b) => b.buildings_administrative || [])
    .reduce((sum, ba) => sum + (ba.unit_nb || 0), 0)
}
